import { FilterOption, filter } from '@order-management/data';
import { formatSalesOrderNumber } from '@order-management/format';
import { userHasPermissionToViewDeviceDetails } from '@order-management/roles';
import {
  Criteria,
  DeviceAssignmentsTableFilterDataType,
  DeviceModel,
  InputType,
  LicensingProductGroup,
  PaginatedContentConfig,
  WorkOrderDetailsDeviceData,
} from '@order-management/types';
import { useCallback, useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import Conditional from '../../shared/conditional/conditional';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useUser from '../../shared/hooks/user';
import Link from '../../ui/link/link';
import PaginatedTable from '../../ui/table/paginated-table/paginated-table';
import { Column, OnTableChangeData } from '../../ui/table/types';
import { filterDevices } from '../filter-modals/filters';
import { getDeviceName } from '../utils';

const NO_SOFTWARE_ASSIGNED = 'No software assigned';

const filterOptions: FilterOption<WorkOrderDetailsDeviceData>[] = [
  {
    getter: (el) => getDeviceName(el),
    searchable: true,
  },
  {
    getter: (el) => formatSalesOrderNumber(el.salesOrderLine.salesOrder.number),
    searchable: true,
  },
  {
    getter: (el) => el.model.displayName,
    searchable: true,
  },
  {
    getter: (el) => el.deviceGroup?.name ?? '-',
    searchable: true,
  },
  {
    getter: (el) =>
      el.assignments
        .filter((assignment) => assignment.softwareSeat)
        .map(
          (assignment) =>
            assignment.softwareSeat.licensingProductGroup.displayName
        )
        .join(', '),
    searchable: true,
  },
];

const initialDeviceAssignmentsCriteria: DeviceAssignmentsTableFilterDataType = {
  deviceTypes: [],
  softwareTitles: [],
  salesOrders: [],
};

export type DeviceAssignmentsTableProps = {
  data: WorkOrderDetailsDeviceData[];
  assignmentsRowTitle: string;
};

const INITIAL_ITEMS_PER_PAGE = 50;

export function DeviceAssignmentsTable({
  data,
  assignmentsRowTitle,
}: DeviceAssignmentsTableProps) {
  const { user } = useUser();
  const [tableCriteria, setTableCriteria] = useState<Criteria>({
    itemsPerPage: INITIAL_ITEMS_PER_PAGE,
    pageNumber: 1,
    search: '',
    sortBy: '',
  });
  const [filterCriteria, setFilterCriteria] =
    useState<DeviceAssignmentsTableFilterDataType>(
      initialDeviceAssignmentsCriteria
    );

  const tableData = useMemo(() => {
    // Filter by filter bar fields
    const barFilteredData = filterDevices(
      data,
      filterCriteria
    ) as WorkOrderDetailsDeviceData[];
    // Filter by table columns
    return filter(barFilteredData, tableCriteria, filterOptions);
  }, [data, filterCriteria, tableCriteria]);

  const tablePageData = useMemo(() => {
    return tableData.slice(
      (tableCriteria.pageNumber - 1) * tableCriteria.itemsPerPage,
      tableCriteria.pageNumber * tableCriteria.itemsPerPage
    );
  }, [tableCriteria.itemsPerPage, tableCriteria.pageNumber, tableData]);

  const fetchDeviceModels = useCallback(() => {
    const deviceModelsMap = data.reduce((acc, device) => {
      const model = device.model;
      if (!acc.has(model.id)) {
        acc.set(model.id, model);
      }
      return acc;
    }, new Map<string, DeviceModel>());
    const deviceModels = Array.from(deviceModelsMap.values());

    return {
      data: deviceModels,
      count: deviceModels.length,
      total: deviceModels.length,
      page: 1,
      pages: 1,
      hasMore: false,
    };
  }, [data]);

  const fetchSoftwareTitles = useCallback(() => {
    const softwareTitlesMap = data.reduce((acc, device) => {
      device.assignments.forEach((assignment) => {
        const licensingProductGroup =
          assignment.softwareSeat.licensingProductGroup;
        if (!acc.has(licensingProductGroup.id)) {
          acc.set(licensingProductGroup.id, licensingProductGroup);
        }
      });
      return acc;
    }, new Map<string, Pick<LicensingProductGroup, 'id' | 'displayName'>>());
    const softwareTitles = Array.from(softwareTitlesMap.values());

    return {
      data: softwareTitles,
      count: softwareTitles.length,
      total: softwareTitles.length,
      page: 1,
      pages: 1,
      hasMore: false,
    };
  }, [data]);

  const filtersData = useMemo(() => {
    return {
      deviceTypes: {
        title: 'Device type',
        appliedCount: filterCriteria.deviceTypes?.length,
        value: filterCriteria.deviceTypes,
        input: {
          type: InputType.SELECT,
          valueKey: 'deviceTypes',
          fetchItems: (_) => Promise.resolve(fetchDeviceModels()),
          resolveOption: (item: DeviceModel) => ({
            label: item.displayName,
            value: item.displayName,
          }),
        },
      },
      softwareTitles: {
        title: 'Software',
        appliedCount: filterCriteria.softwareTitles?.length,
        value: filterCriteria.softwareTitles,
        input: {
          type: InputType.SELECT,
          valueKey: 'softwareTitles',
          fetchItems: (_) => Promise.resolve(fetchSoftwareTitles()),
          resolveOption: (
            item: Pick<LicensingProductGroup, 'id' | 'displayName'>
          ) => ({
            label: item.displayName,
            value: item.displayName,
          }),
        },
      },
      salesOrders: {
        title: 'Sales order',
        appliedCount: filterCriteria.salesOrders?.length,
        value: filterCriteria.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
    };
  }, [
    fetchDeviceModels,
    fetchSoftwareTitles,
    filterCriteria.deviceTypes,
    filterCriteria.salesOrders,
    filterCriteria.softwareTitles,
  ]) as FiltersData<DeviceAssignmentsTableFilterDataType>;

  const handleTableFilterSubmit = useCallback(
    (value: DeviceAssignmentsTableFilterDataType): void => {
      setFilterCriteria(value);
      setTableCriteria((oldCriteria) => ({
        ...oldCriteria,
        pageNumber: 1,
      }));
    },
    []
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof DeviceAssignmentsTableFilterDataType)[]) => {
      const newData = { ...filterCriteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue =
          initialDeviceAssignmentsCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            boolean;
        }
      }
      handleTableFilterSubmit(newData);
    },
    [filterCriteria, handleTableFilterSubmit]
  );

  const appliedFilters = useMemo(() => {
    return Object.entries(filtersData).filter(
      ([_, value]) => value.appliedCount > 0
    );
  }, [filtersData]);

  const tableForm = useMemo(
    () => ({
      enableSearch: false,
    }),
    []
  );

  const tableConfig = useMemo(() => {
    return {
      ...tableCriteria,
      pages: Math.ceil(tableData.length / tableCriteria.itemsPerPage),
    };
  }, [tableCriteria, tableData.length]);

  const columns: Column<WorkOrderDetailsDeviceData>[] = useMemo(
    () => [
      {
        key: 'serialNumber.number',
        className: 'is-width-20',
        render: (el) => {
          let deviceIdentifier = el.serialNumber?.number;
          if (!deviceIdentifier) {
            deviceIdentifier = el.name ?? el.defaultName;
          }
          return (
            <Conditional
              condition={userHasPermissionToViewDeviceDetails(
                user,
                el.salesOrderLine.salesOrder.id
              )}
            >
              <Conditional.True>
                <Link to={`/devices/${el.id}`}>{deviceIdentifier}</Link>
              </Conditional.True>
              <Conditional.False>
                <div>{deviceIdentifier}</div>
              </Conditional.False>
            </Conditional>
          );
        },
        title: 'Identifier',
        sortable: false,
        noWrap: true,
      },
      {
        key: 'salesOrderLine.salesOrder.number',
        className: 'is-width-15',
        render: (el) => (
          <Link to={`/sales-orders/${el.salesOrderLine.salesOrder.id}`}>
            {formatSalesOrderNumber(el.salesOrderLine.salesOrder.number)}
          </Link>
        ),
        title: 'Sales order',
        sortable: true,
      },
      {
        key: 'model.name',
        className: 'is-width-15',
        render: (el) => el.model.displayName,
        title: 'Device type',
        noWrap: true,
      },
      {
        key: 'group',
        className: 'is-width-20',
        render: (el) => el.deviceGroup?.name ?? '',
        title: 'Device group',
      },
      {
        key: 'software',
        className: 'is-width-25',
        render: (el) => {
          const assignedSoftwareTitles = el.assignments
            .filter((assignment) => assignment.softwareSeat)
            .map(
              (assignment) =>
                assignment.softwareSeat.licensingProductGroup.displayName
            );
          if (assignedSoftwareTitles.length === 0) return NO_SOFTWARE_ASSIGNED;
          return assignedSoftwareTitles.join(', ');
        },
        title: assignmentsRowTitle,
      },
    ],
    [assignmentsRowTitle, user]
  );

  const handleTableChange = (
    value: OnTableChangeData<WorkOrderDetailsDeviceData>
  ) => {
    const newTableCriteria: PaginatedContentConfig = {
      ...tableCriteria,
      ...value.config,
      sortBy: value.column?.key || tableCriteria.sortBy,
    };

    setTableCriteria(newTableCriteria);
  };

  const emptyContent = useMemo(() => {
    const emptyContentText =
      appliedFilters.length > 0 || tableConfig.search !== ''
        ? 'There are no devices that match the selected criteria'
        : 'There are no devices at the moment';
    return (
      <Element display="flex" justifyContent="center" className="my-2">
        <span className="has-text-weight-light">{emptyContentText}</span>
      </Element>
    );
  }, [appliedFilters.length, tableConfig.search]);

  return (
    <>
      <Element display="flex" className="w-full pb-2" alignItems="center">
        <FilterBar
          initialData={initialDeviceAssignmentsCriteria}
          data={filterCriteria}
          filtersData={filtersData}
          onFilterSubmit={handleTableFilterSubmit}
          onRemoveFilter={onRemoveFilters}
        />
      </Element>
      <PaginatedTable
        form={tableForm}
        dataSource={tablePageData}
        columns={columns}
        onChange={handleTableChange}
        config={tableConfig}
        rowKey={'id'}
        empty={emptyContent}
      />
    </>
  );
}

export default DeviceAssignmentsTable;
